import { Button, Card, CardContent, CardHeader, CardTitle, Spinner, useFeatureFlag } from '@lensora/ui'
import { useQuery } from '@tanstack/react-query'
import { KeyRound, Plus, ShieldAlert } from 'lucide-react'
import * as React from 'react'

import { CreateKeyModal } from '@/components/mcp/CreateKeyModal'
import { KeyRow } from '@/components/mcp/KeyRow'
import { KeyUsageModal } from '@/components/mcp/KeyUsageModal'
import { LookerGateCard } from '@/components/mcp/LookerGateCard'
import { LookerInstructionsCard } from '@/components/mcp/LookerInstructionsCard'
import { SecretRevealModal } from '@/components/mcp/SecretRevealModal'
import type { CreateMcpKeyResult, McpApiKey } from '@/hooks/use-mcp-keys'
import { useMcpKeys } from '@/hooks/use-mcp-keys'
import { isSelfServeClient, useMe } from '@/hooks/use-me'
import { fetchBillingStatus } from '@/lib/billing'

// ─── Sub-components ────────────────────────────────────────────

function NoPermission() {
  return (
    <Card className="border-feedback-warning/30 bg-feedback-warning/5">
      <CardContent className="flex items-start gap-3 pt-6">
        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-feedback-warning" />
        <p className="text-sm text-text">
          Only workspace owners and admins can manage Looker Studio keys.
        </p>
      </CardContent>
    </Card>
  )
}

function EmptyKeysState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <KeyRound className="mb-4 h-12 w-12 text-text-muted opacity-40" />
      <p className="font-medium text-text">No Looker Studio keys yet</p>
      <p className="mt-1 max-w-sm text-sm text-text-muted">
        Create a key to connect this workspace's report data to Looker Studio.
      </p>
      <Button className="mt-4" onClick={onCreate}>
        <Plus className="mr-2 h-4 w-4" />Create key
      </Button>
    </div>
  )
}

// ─── Page ──────────────────────────────────────────────────────

export function LookerStudioPage() {
  const { data: me, isLoading: meLoading } = useMe()
  const role = me?.user.role
  const isOwnerOrAdmin = role === 'owner' || role === 'admin'
  const isSelfServe = isSelfServeClient(me)
  const canAccess = isOwnerOrAdmin || isSelfServe

  // Tenant-scope (agency owner/admin): gated on a per-tenant feature flag.
  // Confirmed against apps/api/src/routes/mcp/keys.ts and
  // apps/api/src/middleware/looker-api-key.ts, which both read
  // tenant.featureFlags['looker_integration_enabled'].
  const lookerFlagEnabled = useFeatureFlag('looker_integration_enabled')

  // Client-scope (self-serve): gated on plan tier, read from the same
  // billing-status query the white-label/report-export gates use.
  const { data: billingData, isLoading: billingLoading } = useQuery({
    queryKey: ['billing-status'],
    queryFn: fetchBillingStatus,
    enabled: isSelfServe,
    staleTime: 300_000,
  })
  const planTier = billingData?.planTier ?? null
  const planPending = billingLoading || billingData?.planResolved === false
  const hasQualifyingPlan = planTier === 'growth' || planTier === 'enterprise'

  const gatePending = isSelfServe && planPending
  const isGated = isSelfServe ? !hasQualifyingPlan : !lookerFlagEnabled

  const keysEnabled = canAccess && !isGated
  const { data: keys, isLoading: keysLoading } = useMcpKeys(keysEnabled)
  const lookerKeys = (keys ?? []).filter((k) => k.purpose === 'looker_studio')

  const [showCreate, setShowCreate] = React.useState(false)
  const [revealed, setRevealed] = React.useState<CreateMcpKeyResult | null>(null)
  const [usageKey, setUsageKey] = React.useState<McpApiKey | null>(null)

  if (meLoading || gatePending) {
    return <div className="flex h-full items-center justify-center"><Spinner /></div>
  }

  if (!canAccess) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-text">Looker Studio</h2>
          <p className="mt-1 text-sm text-text-muted">Export this workspace's reports to Looker Studio.</p>
        </div>
        <NoPermission />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {showCreate && (
        <CreateKeyModal
          endClients={[]}
          purpose="looker_studio"
          onClose={() => setShowCreate(false)}
          onCreated={(result) => { setShowCreate(false); setRevealed(result) }}
        />
      )}
      {revealed && (
        <SecretRevealModal
          keyName={revealed.key.name}
          keyPrefix={revealed.key.keyPrefix}
          secret={revealed.secret}
          purpose="looker_studio"
          onDismiss={() => setRevealed(null)}
        />
      )}
      {usageKey && (
        <KeyUsageModal keyId={usageKey.id} keyName={usageKey.name} onClose={() => setUsageKey(null)} />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text">Looker Studio</h2>
          <p className="mt-1 text-sm text-text-muted">
            Export this workspace's reports to a Looker Studio dashboard.
          </p>
        </div>
        {!isGated && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="mr-2 h-4 w-4" />Create key
          </Button>
        )}
      </div>

      {isGated ? (
        <LookerGateCard reason={isSelfServe ? 'plan' : 'flag'} />
      ) : (
        <>
          <LookerInstructionsCard />

          {keysLoading ? (
            <div className="flex h-full items-center justify-center"><Spinner /></div>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>API keys</CardTitle>
              </CardHeader>
              <CardContent className="divide-y divide-border p-0">
                {lookerKeys.length === 0 ? (
                  <EmptyKeysState onCreate={() => setShowCreate(true)} />
                ) : (
                  lookerKeys.map((k) => <KeyRow key={k.id} mcpKey={k} onViewUsage={setUsageKey} />)
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
